import React from 'react'
import { Button, Heading, Paragraph } from '~elements'
import dayjs from 'utils/dayjs'
import { FormattedEvent } from 'components/modules/Calendar/types/Event'
import { DATE_FORMATTER } from 'components/modules/Calendar/constants'
import { DATE_FORMAT } from '~constants'
import { TM_CTA_TITLE_DEFAULT } from 'components/modules/Calendar/constants'

interface CalendarModalContentProps {
  ctaOverrides?: {
    title: string
    url: string
  }
  eventInfo: FormattedEvent
  showPriceInModal?: boolean
  shouldAllowSoldOut: boolean
}

const CalendarModalContent = ({
  ctaOverrides,
  eventInfo,
  shouldAllowSoldOut,
  showPriceInModal
}: CalendarModalContentProps) => {
  const { category, dateTime, price, hostUrl, id, isSoldOut } = eventInfo || {}
  const { min: minPrice, max: maxPrice } = price || {}

  const eventPriceRange =
    minPrice && maxPrice
      ? minPrice === maxPrice
        ? `$${minPrice.toFixed(2)}`
        : `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`
      : ''

  const localeTimestamp = dayjs(dateTime)
  const formattedEventDay = localeTimestamp.format(DATE_FORMAT.DAY_NAME)
  const formattedEventDate = localeTimestamp.format(
    `${DATE_FORMAT.MONTH_FULL_NAME} ${DATE_FORMAT.SHORT_DATE}, ${DATE_FORMAT.SHORT_YEAR}`
  )
  const formattedEventTime = localeTimestamp.format(DATE_FORMATTER.TIME)

  const shouldShowPrices = showPriceInModal && !isSoldOut && eventPriceRange

  const getCtaUrl = (hostUrl?: string): string | undefined => {
    if (ctaOverrides && ctaOverrides.url) return ctaOverrides?.url

    if (hostUrl) {
      const url = new URL(hostUrl)
      url.searchParams.append('utm_source', 'sphere')
      url.searchParams.append('utm_medium', 'web')
      url.searchParams.append('camefrom', 'CFC_SPHERE')
      return url.toString()
    }
  }

  const getCtaTitle = () => {
    if (shouldAllowSoldOut && isSoldOut) {
      return TM_CTA_TITLE_DEFAULT.sold_out
    } else if (ctaOverrides && ctaOverrides.title) {
      return ctaOverrides.title
    } else {
      return TM_CTA_TITLE_DEFAULT.onsale
    }
  }

  return (
    <>
      <Heading level={6}>Showtime selected</Heading>

      <Heading level={2} noMarginTop noMarginBottom>
        {formattedEventDay}
        <br />
        {formattedEventDate}
        <br />
        {formattedEventTime} PST
      </Heading>

      {shouldShowPrices && (
        <Paragraph
          text={{ html: `<p>Tickets from ${eventPriceRange}<sup>*</sup></p>` }}
          justify='center'
          fontSize='md'
          noMarginBottom
        />
      )}

      <Paragraph
        text={{ html: '<p>Ticket purchase available through Ticketmaster.</p>' }}
        justify='center'
        fontSize='md'
        noMarginBottom
      />

      <Button
        color='primary'
        fill='solid'
        isTargetBlank
        justify='center'
        size='md'
        title={getCtaTitle()}
        url={getCtaUrl(hostUrl)}
        analytics={{
          linkLocation: 'modal',
          linkModule: 'calendar_modal',
          eventData: {
            eventCategory: category,
            eventDate: localeTimestamp.format(
              `${DATE_FORMAT.SHORT_MM}/${DATE_FORMAT.SHORT_DATE}/${DATE_FORMAT.SHORT_YEAR}`
            ),
            ticketmasterEventId: id
          }
        }}
      />
    </>
  )
}

export default CalendarModalContent
